/**
 * GamePage Component
 *
 * Main game page that orchestrates all game components and manages game flow.
 * Connects to GameContext for state management.
 *
 * Features:
 * - Introduction screen explaining the game
 * - Interactive tutorial mode with step-by-step guidance
 * - Progressive level system (6 levels, 4-15 rooms)
 * - Timer tracking for each level
 * - Level completion and progression
 * - Graph visualization showing room connections
 * - Hint system and undo functionality
 */

import React, { useEffect } from 'react';
import { useGameContext } from '@context';
import { gameActions } from '@context/GameReducer';
import { getLevel } from '@core/levels';
import { getTotalTutorialSteps } from '@core/tutorialSteps';
import { Button } from '@components/common/Button';
import { RoomDisplay } from '@components/game/RoomDisplay';
import { ExitList, Exit } from '@components/game/ExitList';
import { GameStatus } from '@components/game/GameStatus';
import { Timer } from '@components/game/Timer';
import { GraphVisualization } from '@components/game/GraphVisualization';
import { TutorialPanel } from '@components/game/TutorialPanel';
import { IntroPage } from './IntroPage';
import styles from './GamePage.module.scss';

export const GamePage: React.FC = () => {
  const { state, dispatch } = useGameContext();

  // Timer logic - runs when game is playing or lost (continues tracking total time)
  useEffect(() => {
    if (state.gameStatus !== 'playing' && state.gameStatus !== 'lost') return;

    const interval = setInterval(() => {
      dispatch(gameActions.tickTimer());
    }, 1000);

    return () => clearInterval(interval);
  }, [state.gameStatus, dispatch]);

  const handleStartIntro = () => {
    dispatch(gameActions.startIntro());
  };

  const handleStartTutorial = () => {
    dispatch(gameActions.startTutorial());
  };

  const handleStartLevel = (level: number) => {
    dispatch(gameActions.startLevel(level));
  };

  const handleMove = (roomId: number) => {
    dispatch(gameActions.moveToRoom(roomId));
    
    // Auto-advance tutorial step after move
    if (state.isTutorialMode) {
      dispatch(gameActions.nextTutorialStep());
    }
  };

  const handleUndo = () => {
    dispatch(gameActions.undoMove());
  };

  const handleNextLevel = () => {
    const nextLevel = state.currentLevel + 1;
    if (nextLevel <= 6) {
      handleStartLevel(nextLevel);
    } else {
      dispatch(gameActions.resetGame());
    }
  };

  const handleReset = () => {
    dispatch(gameActions.resetGame());
  };

  const handleNextTutorialStep = () => {
    dispatch(gameActions.nextTutorialStep());
  };

  const handleUseHint = () => {
    dispatch(gameActions.useHint());
  };

  const handleHideHint = () => {
    dispatch(gameActions.hideHint());
  };

  const handleExitTutorial = () => {
    dispatch(gameActions.resetGame());
  };

  // Introduction screen
  if (state.gameStatus === 'intro') {
    return <IntroPage onStart={() => handleStartLevel(1)} />;
  }

  // Idle state - show level selection
  if (state.gameStatus === 'idle') {
    return (
      <div className={styles.gamePage}>
        <div className={styles.startScreen}>
          <h1>NP-Complete Escape Room</h1>
          <p className={styles.description}>
            Navigate through procedurally generated rooms to escape!
            Each room you visit locks behind you. Can you find the path that visits all rooms?
          </p>

          <div className={styles.tutorialPrompt}>
            <h2>🎓 New to the game?</h2>
            <p>Start with our interactive tutorial to learn about Hamiltonian Paths and game mechanics!</p>
            <Button
              variant="primary"
              size="large"
              onClick={handleStartTutorial}
            >
              Start Tutorial
            </Button>
          </div>
          
          <div className={styles.levelSelection}>
            <h2>Select Level</h2>
            <div className={styles.levelGrid}>
              {[1, 2, 3, 4, 5, 6].map(level => {
                const levelConfig = getLevel(level);
                if (!levelConfig) return null;
                
                const bestTime = state.bestTimes[level];
                
                return (
                  <div key={level} className={styles.levelCard}>
                    <h3>Level {level}</h3>
                    <p>{levelConfig.numRooms} Rooms</p>
                    <p className={styles.difficulty}>{levelConfig.name}</p>
                    {bestTime && (
                      <p className={styles.bestTime}>
                        Best: {Math.floor(bestTime / 60)}:{(bestTime % 60).toString().padStart(2, '0')}
                      </p>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => handleStartLevel(level)}
                    >
                      Play
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <Button variant="secondary" onClick={handleStartIntro}>
            View Introduction
          </Button>
        </div>
      </div>
    );
  }

  // Level complete screen
  if (state.gameStatus === 'level_complete') {
    const levelConfig = getLevel(state.currentLevel);
    if (!levelConfig) return null;
    
    const isLastLevel = state.currentLevel === 6;
    
    return (
      <div className={styles.gamePage}>
        <div className={styles.levelComplete}>
          <h1>Level {state.currentLevel} Complete!</h1>
          <p className={styles.congratulations}>
            You successfully navigated all {levelConfig.numRooms} rooms!
          </p>
          
          <Timer
            currentTime={state.timeElapsed}
            bestTime={state.bestTimes[state.currentLevel]}
          />

          <div className={styles.actions}>
            {!isLastLevel ? (
              <>
                <Button variant="primary" size="large" onClick={handleNextLevel}>
                  Next Level
                </Button>
                <Button variant="secondary" onClick={handleReset}>
                  Level Select
                </Button>
              </>
            ) : (
              <>
                <h2>Congratulations! You've completed all levels!</h2>
                <Button variant="primary" size="large" onClick={handleReset}>
                  Play Again
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Tutorial or Playing or game over states
  const currentRoom = state.rooms.get(state.currentRoom);
  if (!currentRoom) return null;

  // Build exits list
  const edges = state.edges.get(state.currentRoom) || [];
  const exits: Exit[] = edges.map((roomId: number) => ({
    roomId,
    isLocked: state.visited.has(roomId),
  }));

  const levelConfig = state.isTutorialMode ? null : getLevel(state.currentLevel);

  return (
    <div className={styles.gamePage}>
      {!state.isTutorialMode && (
        <div className={styles.gameHeader}>
          <div className={styles.levelInfo}>
            <h1>Level {state.currentLevel}</h1>
            <p>{levelConfig?.name} - {levelConfig?.numRooms} Rooms</p>
          </div>
          
          <Timer
            currentTime={state.timeElapsed}
            bestTime={state.bestTimes[state.currentLevel]}
          />
        </div>
      )}

      {state.isTutorialMode && (
        <div className={styles.gameHeader}>
          <div className={styles.levelInfo}>
            <h1>🎓 Tutorial Mode</h1>
            <p>Learn the basics of Hamiltonian Paths</p>
          </div>
        </div>
      )}

      {state.isTutorialMode && (
        <TutorialPanel
          currentStep={state.currentTutorialStep}
          totalSteps={getTotalTutorialSteps()}
          showHint={state.showHint}
          hintsRemaining={state.hintsRemaining}
          currentRoom={state.currentRoom}
          solutionPath={state.solutionPath}
          visited={state.visited}
          isTutorialMode={state.isTutorialMode}
          onNextStep={handleNextTutorialStep}
          onUseHint={handleUseHint}
          onHideHint={handleHideHint}
          onExitTutorial={handleExitTutorial}
        />
      )}

      {!state.isTutorialMode && (
        <GameStatus
          status={state.gameStatus}
          visitedCount={state.visited.size}
          totalRooms={state.rooms.size}
        />
      )}

      <GraphVisualization
        roomCount={state.rooms.size}
        edges={state.edges}
        currentRoom={state.currentRoom}
        visited={state.visited}
        solutionPath={state.solutionPath}
        showSolution={false}
      />

      <RoomDisplay room={currentRoom} roomNumber={state.currentRoom} />

      {(state.gameStatus === 'playing' || state.gameStatus === 'tutorial') && (
        <>
          <ExitList exits={exits} onSelectExit={handleMove} />
          
          {state.isTutorialMode && state.moveHistory.length > 1 && (
            <div className={styles.tutorialControls}>
              <Button
                variant="secondary"
                onClick={handleUndo}
              >
                ⏪ Undo Move
              </Button>
            </div>
          )}
        </>
      )}

      {state.gameStatus === 'lost' && (
        <div className={styles.gameOver}>
          <h2>Game Over</h2>
          <p>You got stuck! Try again to find the correct path.</p>
          <div className={styles.actions}>
            <Button variant="primary" size="large" onClick={() => dispatch(gameActions.startLevel(state.currentLevel, false, false))}>
              Retry Level
            </Button>
            <Button variant="secondary" onClick={handleReset}>
              Level Select
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};